// Selecciones incluidas por grupo + extra cobrado (el «mixto»).
//
// FUENTE ÚNICA de la regla, para el cliente (modal, carrito, pantalla del cliente) y
// para el servidor (las rutas que asientan order_item_modifiers). Si el carrito y la
// caja decidieran por separado qué selección es gratis, el cliente vería un total y
// se le cobraría otro — la lección de la pizza por zonas.
//
// Diseño: docs/superpowers/specs/2026-09-18-selecciones-incluidas-mixto-design.md
//
// La regla vive en el GRUPO (`modifier_groups`):
//   - included_selections      N selecciones van incluidas en el precio del plato.
//   - extra_selection_price    precio TARJETA de cada selección más allá de las N.
//   - included_shared_portion  las incluidas, si son 2 o más, van juntas en UNA
//                              porción (el mixto). Sin esto, cada una es su porción.
// y en la OPCIÓN (`modifier_options.uses_all_included`): «va sola» — si se elige,
// ocupa todo el cupo incluido y todas las demás del grupo se cobran como extra
// (Katad: la carne de hamburguesa no se mezcla).
//
// 🔴 El TOTAL no depende del orden de la selección (el extra es uniforme y el recargo
// propio de cada opción se cobra siempre): el orden solo decide QUÉ fila lleva el
// rótulo de extra en la comanda. Por eso el motor es determinista sobre el orden en
// que llegan las selecciones — el orden de toque en pantalla.

export type SelectionRole = "included" | "shared" | "extra"

export const SELECTION_ROLES: readonly SelectionRole[] = ["included", "shared", "extra"]

export function isSelectionRole(v: unknown): v is SelectionRole {
  return typeof v === "string" && (SELECTION_ROLES as readonly string[]).includes(v)
}

/** Regla de un grupo. `extraSelectionPrice` es TARJETA crudo, como `price_delta`. */
export type IncludedSelectionsRule = {
  includedSelections: number
  extraSelectionPrice: number
  sharedPortion: boolean
}

/**
 * Lee la regla de una fila de `modifier_groups` (catálogo del cliente o BD).
 * `null` = el grupo no tiene regla — incluido el caso de una columna AUSENTE porque
 * la migración todavía no corrió: todo lector cae al comportamiento de siempre.
 */
export function includedSelectionsRuleOf(group: {
  included_selections?: number | null
  extra_selection_price?: number | string | null
  included_shared_portion?: boolean | null
  zonable?: boolean | null
}): IncludedSelectionsRule | null {
  // Una línea de pizza por zonas la cotiza ENTERA el motor de zonas; el CHECK de la
  // migración impide la combinación, esto la vuelve inofensiva si igual llega.
  if (group.zonable === true) return null
  const n = Number(group.included_selections)
  if (!Number.isInteger(n) || n < 1) return null
  const price = Number(group.extra_selection_price ?? 0)
  return {
    includedSelections: n,
    extraSelectionPrice: Number.isFinite(price) && price > 0 ? price : 0,
    // Una sola incluida no tiene con quién compartir la porción.
    sharedPortion: group.included_shared_portion === true && n >= 2,
  }
}

/** Una selección de la línea, en orden. */
export type RuleSelection = {
  /** Grupo de la opción. `null` o un grupo sin regla = la fila pasa intacta. */
  groupId: string | null
  /** Unidades de esta opción (2× Pernil = 2). Ausente o inválida = 1. */
  quantity: number
  /** `price_delta` crudo (TARJETA) de la opción, SIN el extra. */
  priceDelta: number
  /** La opción «va sola» (`modifier_options.uses_all_included`). */
  usesAllIncluded: boolean
}

export type RuledRow = {
  /** Índice de la selección de entrada que originó esta fila. */
  source: number
  quantity: number
  /** `null` = grupo sin regla (fila de siempre). */
  role: SelectionRole | null
  /** `price_delta` crudo (TARJETA) por unidad: el de la opción, + el extra si es extra. */
  priceDelta: number
}

function unitsOf(quantity: number): number {
  return Number.isFinite(quantity) && quantity >= 1 ? Math.floor(quantity) : 1
}

/**
 * Aplica las reglas de los grupos a las selecciones de UNA línea.
 *
 * Devuelve `null` si ninguna selección cae en un grupo con regla: el llamador sigue
 * por su camino de siempre, byte por byte. Si no, devuelve TODAS las filas de la
 * línea en el orden de entrada; una selección cuyas unidades cruzan el cupo se parte
 * en dos filas (incluida + extra), igual que la D12 de la pizza por zonas.
 */
export function applyIncludedSelections(
  selections: readonly RuleSelection[],
  rules: ReadonlyMap<string, IncludedSelectionsRule>,
): RuledRow[] | null {
  const ruleOf = (s: RuleSelection) => (s.groupId !== null ? rules.get(s.groupId) : undefined)
  if (!selections.some((s) => ruleOf(s) !== undefined)) return null

  // Rol por unidad: roles[i][k] = rol de la unidad k de la selección i.
  const roles: (SelectionRole | null)[][] = selections.map((s) => new Array(unitsOf(s.quantity)).fill(null))

  const groupsInOrder = [...new Set(selections.filter((s) => ruleOf(s)).map((s) => s.groupId as string))]
  for (const groupId of groupsInOrder) {
    const rule = rules.get(groupId) as IncludedSelectionsRule
    const units: Array<[number, number]> = []
    selections.forEach((s, i) => {
      if (s.groupId !== groupId) return
      for (let k = 0; k < roles[i].length; k++) units.push([i, k])
    })

    const solo = units.find(([i]) => selections[i].usesAllIncluded)
    const included = solo ? [solo] : units.slice(0, rule.includedSelections)
    const includedRole: SelectionRole = !solo && rule.sharedPortion && included.length >= 2 ? "shared" : "included"
    const isIncluded = new Set(included.map(([i, k]) => `${i}:${k}`))
    for (const [i, k] of units) roles[i][k] = isIncluded.has(`${i}:${k}`) ? includedRole : "extra"
  }

  const rows: RuledRow[] = []
  selections.forEach((s, i) => {
    const rule = ruleOf(s)
    for (const role of roles[i]) {
      const last = rows[rows.length - 1]
      if (last && last.source === i && last.role === role) {
        last.quantity += 1
        continue
      }
      // Solo la fila extra se recalcula; las demás llevan el precio de la opción TAL
      // CUAL (price_delta es numeric sin escala: redondearlo cambiaría filas de
      // grupos sin regla que hoy salen idénticas).
      const priceDelta = role === "extra" && rule ? roundCents(s.priceDelta + rule.extraSelectionPrice) : s.priceDelta
      rows.push({ source: i, quantity: 1, role, priceDelta })
    }
  })
  return rows
}

function roundCents(x: number): number {
  return Math.round(x * 100) / 100
}
